import { computed } from 'vue'
import {
  FilterType,
  conversationFiltersState,
  conversationFiltersComputed,
  setPrivateFiltersExpanded,
  setPublicFiltersExpanded,
} from '@klausapp/state/conversations'
import {
  type BaseFilter,
  getPrivateFilters,
  getPublicFilters,
  setPrivateFilterPosition,
  setPublicFilterPosition,
} from '@/modules/conversations/api/filters'
import { valtioBridge } from '@/utils/valtioBridge'
import { hasAccountRole, hasWorkspaceRole } from '@/utils/roleUtils'
import i18n from '@/i18n'
import { track } from '@/utils/analytics'

export { FilterType }

// Vue reactivity bridge over the framework-agnostic state — read through this,
// write to the state directly. Side-effecting logic (i18n / api / analytics)
// stays here; only pure state lives in @klausapp/state.
const filtersState = valtioBridge(conversationFiltersState)
const filtersComputedState = valtioBridge(conversationFiltersComputed)

const filtersInitialized = computed(() => filtersState.value.filtersInitialized)
const activeFilterId = computed(() => filtersState.value.activeFilterId)
const activeFilterName = computed(() => filtersState.value.activeFilterName)
const publicFilters = computed(() => filtersState.value.publicFilters as BaseFilter[])
const privateFilters = computed(() => filtersState.value.privateFilters as BaseFilter[])
const allFilters = computed(() => filtersComputedState.value.allFilters as BaseFilter[])
const publicFiltersExpanded = computed(() => filtersState.value.publicFiltersExpanded)
const publicFiltersActive = computed(() => filtersComputedState.value.publicFiltersActive)
const privateFiltersExpanded = computed(() => filtersState.value.privateFiltersExpanded)
const privateFiltersActive = computed(() => filtersComputedState.value.privateFiltersActive)
const searchModeActive = computed(() => filtersComputedState.value.searchModeActive)

const setActiveFilterId = (id: number | string | null) => {
  if (id) updateActiveFilterName(id)

  conversationFiltersState.activeFilterId = id
}

const setActiveFilterName = (name: string) => {
  conversationFiltersState.activeFilterName = name
}

const expandPublicFilters = (expanded = true) => {
  setPublicFiltersExpanded(expanded)
  track(`Filters ${expanded} ? 'expanded' : 'collapsed'`, { isPublic: true })
}
const expandPrivateFilters = (expanded = true) => {
  setPrivateFiltersExpanded(expanded)
  track(`Filters ${expanded} ? 'expanded' : 'collapsed'`, { isPublic: false })
}

export const setPrivateFilters = (newFilters: BaseFilter[]) => {
  conversationFiltersState.privateFilters = newFilters
}
export const setPublicFilters = (newFilters: BaseFilter[]) => {
  conversationFiltersState.publicFilters = newFilters
}

const reorderLiveFilters = <T extends { filterId: number }>(
  orderedFilters: readonly Pick<BaseFilter, 'filterId'>[],
  currentFilters: readonly T[],
): T[] => {
  const currentFiltersById = new Map(currentFilters.map((filter) => [filter.filterId, filter]))

  return orderedFilters.flatMap(({ filterId }) => {
    const filter = currentFiltersById.get(filterId)
    return filter ? [filter] : []
  })
}

const reorderPublicFilters = (orderedFilters: readonly Pick<BaseFilter, 'filterId'>[]) => {
  conversationFiltersState.publicFilters = reorderLiveFilters(orderedFilters, conversationFiltersState.publicFilters)
}

const reorderPrivateFilters = (orderedFilters: readonly Pick<BaseFilter, 'filterId'>[]) => {
  conversationFiltersState.privateFilters = reorderLiveFilters(orderedFilters, conversationFiltersState.privateFilters)
}

const getFilters = async (defaultToFirstFilter = false) => {
  if (hasAccountRole('ADMIN', 'MANAGER') || hasWorkspaceRole('MANAGER', 'LEAD', 'REVIEWER')) {
    const publicData = await getPublicFilters().then(({ data }) => data)
    setPublicFilters(Array.isArray(publicData) ? publicData : [])
  }
  const privateData = await getPrivateFilters().then(({ data }) => data)
  setPrivateFilters(Array.isArray(privateData) ? privateData : [])
  conversationFiltersState.filtersInitialized = true

  updateActiveFilterName()

  if (!activeFilterId.value || !activeFilterName.value || defaultToFirstFilter) selectDefaultFilter()
}

export const selectDefaultFilter = () => {
  setActiveFilterId((publicFilters.value[0] || privateFilters.value[0] || {}).filterId)
}

export const handlePublicFiltersNewOrder = ({ newIndex, oldIndex }) => {
  const filter = conversationFiltersState.publicFilters[newIndex]

  if (newIndex !== oldIndex) {
    setPublicFilterPosition({
      id: filter.filterId,
      position: newIndex,
    })
  }

  track('Filter dragged', { isPublic: true })
}

export const handlePrivateFiltersNewOrder = ({ newIndex, oldIndex }) => {
  const filter = conversationFiltersState.privateFilters[newIndex]

  if (newIndex !== oldIndex) {
    setPrivateFilterPosition({
      id: filter.filterId,
      position: newIndex,
    })
  }

  track('Filter dragged', { isPublic: false })
}

const getFilterName = (id: string | number): string => {
  if (typeof id === 'string') {
    if (id === FilterType.Starred) return i18n.t('conversations.sub_nav.starred')
    if (id === FilterType.Drafts) return i18n.t('conversations.filters.drafts')
    if (id.startsWith(FilterType.Search)) return i18n.t('universal.search')

    return id
  }

  return allFilters.value.find((f) => f.filterId === id)?.name || ''
}

const updateActiveFilterName = (id?: number | string) => {
  if (!id) id = activeFilterId.value

  setActiveFilterName(getFilterName(id))
}

export {
  filtersInitialized,
  activeFilterId,
  activeFilterName,
  publicFilters,
  privateFilters,
  allFilters,
  publicFiltersExpanded,
  publicFiltersActive,
  privateFiltersExpanded,
  privateFiltersActive,
  searchModeActive,
  setActiveFilterId,
  updateActiveFilterName,
  expandPublicFilters,
  expandPrivateFilters,
  reorderPublicFilters,
  reorderPrivateFilters,
  getFilters,
}
